import { useState } from "react";
import confetti from "canvas-confetti";
import { CATEGORIES, todayStr, dayOfYear } from "../constants/categories";
import { CHALLENGE_POOL } from "../constants/challengePool";
import {
  saveChallengeAPI, updateChallengeAPI, updateStreakAPI,
  generateChallengeAI, generateReflectionAI,
  buildChallengeRecord,
} from "../services/apiService";
import {
  trackChallengeGenerated, trackChallengeCompleted,
  trackReflectionWritten,
} from "../services/analytics";

const REFLECTION_FALLBACKS = [
  "Tuyệt vời! Mỗi bước nhỏ hôm nay là nền tảng cho sự thay đổi lớn ngày mai. 🌟",
  "Bạn đã chọn hành động thay vì ngồi yên — đó là điều phi thường rồi! 💪",
  "Không cần hoàn hảo, chỉ cần tiến lên. Và bạn vừa làm được điều đó. 🚀",
  "Hôm nay bạn đã gieo một hạt giống. Hãy kiên nhẫn — nó sẽ nở hoa. 🌱",
  "Người thành công không làm điều phi thường — họ làm điều bình thường, mỗi ngày. ✨",
  "Bạn vừa chứng minh rằng mình có kỷ luật hơn 99% người khác. Tiếp tục nhé! 🔥",
  "2 phút nhỏ bé, nhưng hiệu ứng tích lũy sẽ thay đổi cả cuộc đời bạn. 📈",
  "Điều quan trọng nhất không phải kết quả — mà là bạn đã bắt đầu. 🎯",
  "Mỗi ngày bạn đều trở thành phiên bản tốt hơn một chút. Cứ thế nhé! 🌈",
  "Cảm ơn bạn đã dành thời gian cho chính mình hôm nay. Bạn xứng đáng! 💖",
  "Thói quen nhỏ, thay đổi lớn. Bạn đang đi đúng hướng rồi! 🏆",
];

export function useChallenge({ streak, setStreak, setHistory }) {
  const [challenge, setChallenge] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  const [todayChallenges, setTodayChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [selectedCat, setSelectedCat] = useState(CATEGORIES[dayOfYear % CATEGORIES.length]);

  // Reflection
  const [reflectionInput, setReflectionInput] = useState("");
  const [reflection, setReflection] = useState(null);
  const [loadingReflect, setLoadingReflect] = useState(false);

  const saveNewChallenge = async (challengeObj) => {
    const record = buildChallengeRecord(challengeObj, selectedCat, todayStr, challengeObj.completed || false, null, null);
    try {
      const saved = await saveChallengeAPI(record);
      if (saved?.id) setChallengeId(saved.id);
      setTodayChallenges(prev => [...prev, saved]);
      setHistory(prev => {
        const filtered = prev.filter(h => h.id !== saved.id);
        return [saved, ...filtered].sort((a, b) => b.date.localeCompare(a.date));
      });
      return saved;
    } catch (e) {
      console.error("Lỗi lưu challenge:", e);
      return null;
    }
  };

  const updateCurrentChallenge = async (completed, reflectionText = null, aiFeedback = null) => {
    if (!challengeId) return;
    try {
      const updated = await updateChallengeAPI(challengeId, { completed, reflection: reflectionText, ai_feedback: aiFeedback });
      setTodayChallenges(prev => prev.map(c => c.id === challengeId ? updated : c));
      setHistory(prev => prev.map(h => h.id === challengeId ? { ...h, ...updated } : h));
    } catch (e) {
      console.error("Lỗi cập nhật challenge:", e);
    }
  };

  const updateStreak = async () => {
    try {
      const data = await updateStreakAPI(todayStr);
      setStreak(data);
    } catch (e) {
      console.error("Update streak failed:", e);
    }
  };

  const fireConfetti = () => {
    const end = Date.now() + 1500;
    const colors = ["#10B981", "#3B82F6", "#F59E0B", "#EC4899", "#8B5CF6"];
    (function frame() {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const generateChallenge = async () => {
    setLoading(true);
    setChallenge(null);
    setChallengeId(null);
    setDone(false);
    setReflection(null);
    setReflectionInput("");
    try {
      const parsed = await generateChallengeAI(selectedCat, todayStr);
      setChallenge(parsed);
      trackChallengeGenerated(selectedCat);
    } catch {
      const pool = CHALLENGE_POOL[selectedCat.id] || CHALLENGE_POOL.think;
      const fallback = pool[Math.floor(Math.random() * pool.length)];
      setChallenge(fallback);
    }
    setLoading(false);
  };

  const markDone = async () => {
    setDone(true);
    fireConfetti();
    trackChallengeCompleted(selectedCat, streak.current_streak);
    if (!challengeId) {
      const saved = await saveNewChallenge({ ...challenge, completed: true });
      if (saved) setChallengeId(saved.id);
    } else {
      await updateCurrentChallenge(true, reflectionInput || null, reflection);
    }
    await updateStreak();
  };

  const generateReflection = async () => {
    if (!reflectionInput.trim()) return;
    setLoadingReflect(true);
    try {
      const text = await generateReflectionAI(challenge?.title, reflectionInput);
      setReflection(text);
      trackReflectionWritten(true);
      if (challengeId) await updateCurrentChallenge(done, reflectionInput, text);
    } catch {
      const fallback = REFLECTION_FALLBACKS[Math.floor(Math.random() * REFLECTION_FALLBACKS.length)];
      setReflection(fallback);
      if (challengeId) await updateCurrentChallenge(done, reflectionInput, fallback);
    }
    setLoadingReflect(false);
  };

  const saveReflection = async () => {
    if (challengeId) await updateCurrentChallenge(done, reflectionInput, reflection);
  };

  const resetChallenge = () => {
    setChallenge(null);
    setChallengeId(null);
    setDone(false);
    setReflection(null);
    setReflectionInput("");
  };

  return {
    challenge, loading, done, selectedCat, setSelectedCat,
    todayChallenges, setTodayChallenges,
    reflectionInput, setReflectionInput, reflection, loadingReflect,
    generateChallenge, markDone, resetChallenge,
    generateReflection, saveReflection,
  };
}
